import type { BlockKind } from '@agos/shared'

/**
 * Blocking a bad order's contacts from the archive - `FRONTEND.md` §9.787, on BACKEND.md §10A.439, owner O140: *"zablokovať
 * email/telefonné číslo - či IP adresu, odkiaľ prišla objednávka"*.
 *
 * The order's own phone, e-mail and address, each offered only when the order carries it: the server reads them
 * itself and refuses one it does not have, so offering it would invite a refusal. The address is on the row only
 * since §10A.439 and only for a web order, which is why it is read untyped until the client is regenerated.
 */

export interface OrderContacts {
  phone: string | null
  email: string | null
  ip: string | null
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value.trim() : null
}

export function orderContactsOf(order: unknown): OrderContacts {
  const row = typeof order === 'object' && order !== null ? (order as Record<string, unknown>) : {}
  return { phone: text(row.customerPhone), email: text(row.customerEmail), ip: text(row.clientIp) }
}

/** The kinds this order can be blocked by, in the order the dialog draws them. */
export function blockableKinds(contacts: OrderContacts): BlockKind[] {
  const kinds: BlockKind[] = []
  if (contacts.phone !== null) kinds.push('phone')
  if (contacts.email !== null) kinds.push('email')
  if (contacts.ip !== null) kinds.push('ip')
  return kinds
}

/**
 * What the dialog starts with ticked: the phone and the e-mail, which are the strong blocks. The address is offered and
 * not ticked, because one address can be many customers on a mobile network.
 */
export function defaultTicks(contacts: OrderContacts): Record<BlockKind, boolean> {
  return { phone: contacts.phone !== null, email: contacts.email !== null, ip: false }
}

/** The request `POST /orders/{id}/block` takes. */
export function orderBlockBody(
  ticks: Record<BlockKind, boolean>,
  reason: string,
): { phone: boolean; email: boolean; ip: boolean; reason: string | null } {
  return { phone: ticks.phone, email: ticks.email, ip: ticks.ip, reason: reason.trim() === '' ? null : reason.trim() }
}
